import GolfIcon from '../components/GolfIcon';
import '../styles/live-game.css';
import '../styles/clubhouse.css';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { gamesApi, walletApi, statsApi, Game, Wallet, MyStats } from '../api';
import { 
  Wallet as WalletIcon, Calendar, Users,
  TrendingUp, Award, ChevronRight, Zap,
  Flag, Flame
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, Button } from '../components/ui';
import { parseSAST, formatSAST } from '../utils/datetime';
import { formatHandicapIndex } from '../utils/handicapIndex';

const Home: React.FC = () => {
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [stats, setStats] = useState<MyStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [gamesData, walletData] = await Promise.all([
          gamesApi.getMyGames(),
          walletApi.getWallet(),
        ]);
        setGames(Array.isArray(gamesData) ? gamesData : []);
        setWallet(walletData && typeof walletData === 'object' ? walletData : null);
        statsApi.getMyStats().then(setStats).catch(() => {});
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const upcomingGames = games.filter(game =>
    parseSAST(game.scheduled_date) > new Date() && game.status !== 'completed'
  ).sort((a, b) => parseSAST(a.scheduled_date).getTime() - parseSAST(b.scheduled_date).getTime());

  const welcomeMessages = [
    "Ready to lose some money?",
    "Time to embarrass yourself on the course.",
    "Another day, another chance to shank it.",
    "Golf is a good walk spoiled — make it profitable.",
    "Let’s make some bad decisions and worse shots."
  ];

  const randomWelcome = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];

  const completedGames = games.filter(g => g.status === 'completed');
  const inProgressGame = games.find(g => g.status === 'in_progress');

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Layout>
      <div className="space-y-6 pb-24">
        {/* Hero Section with Golf Image */}
        <div className="relative overflow-hidden rounded-3xl shadow-lg">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/images/fourball-banter-v2.webp)' }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          <div className="relative z-10 p-6 pt-16 text-white">
            <p className="text-white/80 text-sm mb-1">{getGreeting()}</p>
            <h1 className="text-2xl font-bold mb-1">
              {user ? `Hi, ${user.full_name?.split(' ')[0]}` : 'Ready for a round?'}
            </h1>
            <p className="text-white/70 text-sm mb-2">
              {typeof user?.handicap === 'number' ? `Handicap Index: ${formatHandicapIndex(user.handicap)}` : 'Ready for your next round?'}
            </p>
            <p className="text-white/60 text-xs italic">{randomWelcome}</p>
          </div>
          {inProgressGame && (
            <Link
              to={`/game/${inProgressGame.id}`}
              className="live-frame relative z-10 mx-6 mb-4 flex items-center justify-between bg-brand-950/80 backdrop-blur-sm rounded-xl p-3 hover:bg-brand-900/80 transition overflow-hidden"
            >
              {/* Light travelling along each edge of the frame */}
              <span className="lf-line lf-top" />
              <span className="lf-line lf-right" />
              <span className="lf-line lf-bottom" />
              <span className="lf-line lf-left" />
              {/* Golf ball rolls across once when the live card appears */}
              <span className="lf-ball" aria-hidden="true" />
              <div className="flex items-center space-x-3">
                <div className="bg-white/15 p-2 rounded-lg">
                  <Zap className="live-bolt h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-white/70">Live Game</p>
                  <p className="font-semibold text-white">{inProgressGame.name}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-white" />
            </Link>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3 home-shortcuts">
          <Link to="/quick-game" className="block">
            <Card className="flex flex-col items-center p-3 hover:border-[#cdbf9d] hover:shadow-md transition">
              <GolfIcon name="flag" />
              <span className="text-xs font-medium text-gray-700">Quick Game</span>
            </Card>
          </Link>
          <Link to="/wallet" className="block">
            <Card className="flex flex-col items-center p-3 hover:border-[#cdbf9d] hover:shadow-md transition">
              <GolfIcon name="wallet" />
              <span className="text-xs font-medium text-gray-700">Deposit</span>
            </Card>
          </Link>
          <Link to="/game" className="block">
            <Card className="flex flex-col items-center p-3 hover:border-[#cdbf9d] hover:shadow-md transition">
              <GolfIcon name="trophy" />
              <span className="text-xs font-medium text-gray-700">Games</span>
            </Card>
          </Link>
          <Link to="/golfers" className="block">
            <Card className="flex flex-col items-center p-3 hover:border-[#cdbf9d] hover:shadow-md transition">
              <GolfIcon name="mates" />
              <span className="text-xs font-medium text-gray-700">Golfers</span>
            </Card>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card padding="lg">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-[#e6eee9] p-2 rounded-xl">
                <WalletIcon className="h-6 w-6 text-brand-700" />
              </div>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-xs text-gray-500 mb-1">Wallet Balance</p>
            <p className="text-2xl font-bold text-gray-800">
              R{wallet?.balance_zar?.toFixed(0) || '0'}
            </p>
            <Link to="/wallet" className="text-xs text-emerald-600 font-medium hover:underline mt-2 inline-block">
              Manage wallet →
            </Link>
          </Card>

          <Card padding="lg">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-[#eee8d9] p-2 rounded-xl">
                <Award className="h-6 w-6 text-[#8b7138]" />
              </div>
              <Flag className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-xs text-gray-500 mb-1">Played / Won</p>
            <p className="text-2xl font-bold text-gray-800">
              {stats?.games_completed ?? completedGames.length}
              <span className="text-gray-300"> / </span>
              <span className="text-emerald-600">{stats?.wins ?? 0}</span>
            </p>
            <p className="text-xs text-gray-400 mt-2">
              {stats ? `${Math.round((stats.win_rate || 0) * 100)}% win rate` : `${upcomingGames.length} upcoming`}
              {stats && stats.current_win_streak > 0 && (
                <span className="inline-flex items-center gap-1 ml-1">
                  <Flame className="h-3 w-3 text-amber-500" />
                  {stats.current_win_streak}
                </span>
              )}
            </p>
          </Card>
        </div>

        {/* Upcoming Games Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Upcoming Games</h2>
            <Link to="/game" className="text-sm text-emerald-600 font-medium hover:underline">
              See all
            </Link>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : upcomingGames.length === 0 ? (
            <Card variant="flat" padding="lg" className="text-center p-8">
              <img src="/images/empty-games.png" alt="" className="w-28 h-28 object-contain mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">No games scheduled</h3>
              <p className="text-sm text-gray-500 mb-4">
                Create a game and invite your fourball to get started
              </p>
              <Link to="/quick-game">
                <Button leftIcon={<Zap className="h-5 w-5" />}>Quick Game</Button>
              </Link>
            </Card>
          ) : (
            <div className="space-y-3">
              {upcomingGames.slice(0, 3).map((game) => (
                <Link key={game.id} to={`/game/${game.id}`} className="block">
                  <Card className="p-4 hover:border-emerald-300 hover:shadow-md transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-800">{game.name}</h3>
                        {game.status === 'in_progress' && (
                          <span className="flex items-center space-x-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                            <span>Live</span>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{game.course_name}</p>
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center text-xs text-gray-400">
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          {formatSAST(game.scheduled_date, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })} SAST
                        </span>
                        <span className="flex items-center text-xs text-gray-400">
                          <Flag className="h-3.5 w-3.5 mr-1" />
                          {game.scoring_format}
                        </span>
                        <span className="flex items-center text-xs text-gray-400">
                          <Users className="h-3.5 w-3.5 mr-1" />
                          {game.player_count}/4
                        </span>
                      </div>
                    </div>
                      <ChevronRight className="h-5 w-5 text-gray-300" />
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Betting Tip */}
        <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
          <div className="flex items-start space-x-3">
            <div className="bg-emerald-100 p-2 rounded-xl">
              <Zap className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Pro golfer tip</h3>
              <p className="text-sm text-gray-600">
                Create a game in under 2 minutes. The worst shots make the best stories.
              </p>
            </div>
          </div>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-xs text-gray-400">
          <p>Fairplay: turning friendships into financial transactions since 2026.</p>
        </div>
      </div>
    </Layout>
  );
};

export default Home;
